// src/pages/PaymentPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  Lock,
  Loader2,
  Clock,
  FileText,
  Sparkles,
} from 'lucide-react';
import { track } from '../utils/track';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

const COMPARE_ROWS = [
  { label: 'Cost', us: null, consultant: '$2,500 - $10,000', diy: '$0 + your time' },
  { label: 'Time to first insight', us: 'Minutes', consultant: '2-4 weeks', diy: 'Weeks of research' },
  { label: 'Personalized to your business', us: 'Yes', consultant: 'Yes', diy: 'No' },
  { label: 'Tools vetted for SMBs', us: 'Yes', consultant: 'Varies', diy: 'No' },
  { label: 'Prompt library included', us: 'Yes', consultant: 'No', diy: 'No' },
  { label: '90-day action plan', us: 'Yes', consultant: 'Yes', diy: 'No' },
  { label: 'Money-back guarantee', us: '60 days', consultant: 'Rarely', diy: 'N/A' },
];

// Testimonials removed — awaiting real customer results

const PaymentPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState(149);


  // Fetch live price
  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/current`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  // Fetch report + prefill promo from query
  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/audit/report/${reportId}`
        );
        if (!response.ok) {
          toast.error('Report not found');
          return;
        }

        const data = await response.json();
        setReportData(data);

        if (data.isPaid) {
          navigate(`/full-report/${reportId}`);   // already paid, no building screen needed
          return;
        }

        // If promo in URL, prefill and let user apply
        const params = new URLSearchParams(location.search);
        const urlPromo = params.get('promo');
        if (urlPromo) {
          setPromoCode(urlPromo.toUpperCase());
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Failed to load report details');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId, navigate, location.search]);

  // Base price from backend (in dollars) – fallback to 149
  const basePrice =
    typeof reportData?.price === 'number' ? reportData.price : 149;

  const handleApplyPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) {
      toast.error('Please enter a promo code.');
      return;
    }

    setIsApplyingPromo(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/payment/apply-promo`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportId, promoCode: code }),
        }
      );

      const data = await response.json();
      if (data.valid) {
        setDiscount(data.discount || 0);

        const finalPriceInDollars =
          typeof data.finalPrice === 'number'
            ? data.finalPrice / 100
            : basePrice * (1 - (data.discount || 0) / 100);

        setFinalPrice(finalPriceInDollars);
        toast.success(data.message || 'Promo applied.');

        if (data.fullReportAvailable) {
          // GA4 ecommerce: 100%-off promo path. Bypasses Stripe entirely,
          // so the success page never fires. Recording it as a $0 purchase
          // here keeps the conversion funnel complete in analytics. Google
          // Ads should be configured with $0 conversions de-prioritized
          // (or excluded) so promo unlocks don't pollute Smart Bidding —
          // but we still want to count them as funnel completions.
          track('purchase', {
            transaction_id: `promo_${reportId}_${Date.now()}`,
            currency: 'USD',
            value: 0,
            items: [{
              item_id: reportId,
              item_name: `AI Implementation Blueprint (Promo Unlock)`,
              item_category: 'AI Blueprint',
              price: 0,
              quantity: 1,
            }],
          });
          setTimeout(
            () => navigate(`/full-report/${reportId}?just_unlocked=true`),
            1500
          );
        }
      } else {
        toast.error(data.message || 'Invalid promo code.');
        setDiscount(0);
        setFinalPrice(null);
      }
    } catch (error) {
      console.error('apply-promo error:', error);
      toast.error('Failed to apply promo code.');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await fetch(
        `${API_BASE}/api/payment/create-checkout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportId, originUrl }),
        }
      );

      if (!response.ok) throw new Error('Failed to create checkout');
      const data = await response.json();

      if (data.checkoutUrl) {
        // GA4 ecommerce: begin_checkout — fires the moment Stripe receives
        // a real card-paying buyer. This is the second-to-last funnel step
        // before "purchase" and is what Google Ads optimizes against if
        // purchase tracking is delayed by Stripe's webhook latency.
        const _checkoutPrice = finalPrice !== null ? finalPrice : basePrice;
        track('begin_checkout', {
          currency: 'USD',
          value: _checkoutPrice,
          items: [{
            item_id: reportId,
            item_name: `AI Implementation Blueprint - ${reportData?.industry || 'Custom'}`,
            item_category: 'AI Blueprint',
            price: _checkoutPrice,
            quantity: 1,
            ...(discount > 0 ? { discount: Math.round((basePrice - _checkoutPrice) * 100) / 100 } : {}),
          }],
        });
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('create-checkout error:', error);
      toast.error(
        'Failed to initiate checkout. Please try again.'
      );
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  const displayPrice =
    finalPrice !== null ? finalPrice : basePrice;
  const displayDiscount = discount > 0;
  const savedAmount = Math.max(0, basePrice - displayPrice);
  const formattedDisplayPrice = Number(
    displayPrice
  ).toFixed(2);
  const formattedBasePrice = Number(basePrice).toFixed(2);
  const formattedSaved = Number(savedAmount).toFixed(2);

  const businessName =
    reportData?.businessName || 'Your Business';

  return (
    <div className="min-h-screen bg-[#0f172a] font-sans text-white">
      <Header />

      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-4 py-1">
              AI Implementation Blueprint Checkout
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              Unlock Your AI Implementation Blueprint
            </h1>
            <p className="text-2xl text-[#3b82f6]">
              For {businessName}
            </p>
            <p className="text-slate-400 mt-2">
              Personalized 90‑day roadmap • AI Playbooks • Tool stack •
              Prompt library • Risk & ROI map
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* LEFT: Value Stack */}
            <div className="space-y-6">
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 overflow-hidden">
                <div className="bg-blue-500/15 border-b border-blue-500/20 p-4 text-slate-300 text-center text-sm">
                  A single AI consulting session runs $300–$500/hr.
                  This blueprint covers what 4–6 hours of consulting would —
                  plus tools, prompts, and a 90-day plan you keep forever.
                </div>
                <CardHeader className="pb-4">
                  <CardTitle className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        AI Implementation Blueprint (Web + PDF)
                      </h3>
                      <p className="text-slate-400 font-medium mt-1">
                        One‑time payment, lifetime access to this version
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-4xl font-bold text-white">
                        ${formattedDisplayPrice}
                      </span>
                      {displayDiscount && (
                        <span className="block text-sm text-slate-400 line-through">
                          ${formattedBasePrice}
                        </span>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    {[
                      'Complete 3‑phase AI roadmap tuned to your answers (90‑day plan).',
                      '3–5 launch‑ready AI Playbooks that bundle problems, tools, and step‑by‑step workflows.',
                      'Full PDF blueprint with industry benchmarks, risk map, and ROI projections.',
                      'Vetted AI tool stack + “hidden‑gem” tools, including pricing and quick‑start setup steps.',
                      'Custom prompt library written for your exact problems and preferred AI assistant.',
                      '90‑Day Adoption Checklist so your team actually implements instead of letting this sit on a shelf.',
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3"
                      >
                        <div className="mt-1 bg-blue-500/20 p-1 rounded-full">
                          <CheckCircle2 className="w-4 h-4 text-[#3b82f6]" />
                        </div>
                        <span className="text-slate-300">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-blue-900/30 border border-blue-500/30 rounded-2xl p-5">
                    <p className="text-[#3b82f6] text-sm font-medium mb-1">
                      60‑day money‑back guarantee
                    </p>
                    <p className="text-slate-200 text-sm">
                      If this blueprint doesn’t help you save time, get
                      clarity, or move forward on AI within 60 days,
                      email us and we’ll refund you. No questions asked.
                    </p>
                  </div>

                  <div className="bg-slate-950/50 border border-white/8 rounded-2xl p-4 text-sm text-slate-300">
                    <p>
                      <span className="font-semibold text-white">
                        What you’ll receive instantly:
                      </span>
                    </p>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>
                        Web version of your AI Implementation Blueprint
                        (interactive view).
                      </li>
                      <li>
                        Downloadable PDF blueprint (optimized for
                        printing and sharing with your team).
                      </li>
                      <li>
                        All sections: AI Playbooks, 3‑phase roadmap,
                        vetted tool stack, emerging tools, prompt
                        library, and 90‑Day Adoption Checklist.
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Comparison Table */}
              <Card className='bg-slate-900/70 backdrop-blur-xl border border-white/10 overflow-hidden'>
                <div className='px-5 pt-4 pb-2'>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3'>How this compares</p>
                </div>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-y border-white/8 bg-slate-950/50'>
                        <th className='text-left py-2 px-4 text-slate-400 font-medium' />
                        <th className='text-center py-2 px-3 text-[#3b82f6] font-bold'>This Blueprint</th>
                        <th className='text-center py-2 px-3 text-slate-400 font-medium'>AI Consultant</th>
                        <th className='text-center py-2 px-3 text-slate-400 font-medium'>DIY Research</th>
                      </tr>
                    </thead>
                    <tbody>
                      {COMPARE_ROWS.map(({ label, us, consultant, diy }) => (
                        <tr key={label} className='border-b border-white/8 hover:bg-white/4'>
                          <td className='py-2.5 px-4 text-slate-300 font-medium'>{label}</td>
                          <td className='py-2.5 px-3 text-center text-[#3b82f6] font-semibold'>{us === null ? `$${currentPrice}` : us}</td>
                          <td className='py-2.5 px-3 text-center text-slate-400'>{consultant}</td>
                          <td className='py-2.5 px-3 text-center text-slate-400'>{diy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Testimonials — awaiting real customer results */}
              <p className="text-center text-slate-500 text-sm py-6">
                We launched recently — real customer results coming soon.
                <br />
                <span className="text-slate-600">60-day money-back guarantee if it doesn't deliver value.</span>
              </p>
            </div>
            {/* RIGHT: Payment Form */}
            <div className="space-y-6">
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#3b82f6]" />{' '}
                    Secure Checkout
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Promo Code */}
                  <div className="flex gap-2">
                    <Input
                      id="promo-code"
                      aria-label="Promo code"
                      placeholder="Promo Code"
                      value={promoCode}
                      onChange={(e) =>
                        setPromoCode(
                          e.target.value.toUpperCase()
                        )
                      }
                      className="bg-slate-950/50 border-white/15 text-white"
                    />
                    <Button
                      onClick={handleApplyPromoCode}
                      variant="outline"
                      disabled={isApplyingPromo}
                      className="border-white/15 text-slate-300 hover:bg-white/8 rounded-full"
                    >
                      {isApplyingPromo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>

                  {displayDiscount && (
                    <div className="text-center text-[#3b82f6] font-medium bg-blue-900/30 p-3 rounded-xl border border-blue-500/30">
                      Promo applied! You saved ${formattedSaved}
                    </div>
                  )}

                  {/* Pay Button */}
                  <Button
                    onClick={handleStripeCheckout}
                    disabled={isProcessing}
                    className="w-full h-14 text-lg font-bold bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 shadow-xl rounded-full hover:shadow-blue-500/30 hover:scale-[1.02] transition-all"
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="animate-spin" /> Redirecting
                        to secure checkout...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Lock className="w-5 h-5" /> Pay Securely $
                        {formattedDisplayPrice}
                      </span>
                    )}
                  </Button>

                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-3">
                      Guaranteed Safe & Secure Checkout • Instant
                      Access
                    </p>
                    <div className="flex justify-center gap-4 opacity-70">
                      <img
                        src="/logos/visa.svg"
                        className="h-5"
                        alt="Visa"
                      />
                      <img
                        src="/logos/mastercard.svg"
                        className="h-5"
                        alt="Mastercard"
                      />
                      <img
                        src="/logos/amex.svg"
                        className="h-5"
                        alt="American Express"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trust / Summary */}
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
                <CardContent className="p-5 space-y-3 text-sm text-slate-300">
                  <div className="flex items-center gap-2 text-slate-200">
                    <FileText className="w-4 h-4 text-[#3b82f6]" />
                    <span>
                      <span className="font-semibold">
                        Includes:
                      </span>{' '}
                      full web + PDF blueprint, AI Playbooks, 3‑phase
                      roadmap, vetted tool stack, emerging tools, prompt
                      library, and 90‑Day Adoption Checklist.
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#3b82f6]" />
                    <span>
                      Built from your inputs plus industry benchmarks
                      and proprietary SMB data — not a generic template.
                    </span>
                  </div>
                  <div className="flex justify-center gap-8 text-sm text-slate-400 pt-2 border-t border-white/8 mt-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-[#3b82f6]" />{' '}
                      SSL Encrypted
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#3b82f6]" />{' '}
                      Instant Delivery
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentPage;
